import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import axios from 'axios';

const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://moga-dev.com/api';

export const fetchUserCart = createAsyncThunk(
  'cart/fetchUserCart',
  async (_, { rejectWithValue, getState }) => {
    try {
      const userId = getState().auth.user?.id;
      if (!userId) {
        return rejectWithValue('User not authenticated');
      }

      const response = await axios.get(`${baseUrl}/cart/${userId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const getLocalCart = () => {
  try {
    const stored = localStorage.getItem("guest_cart");
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

export const saveLocalCart = (items) => {
  localStorage.setItem("guest_cart", JSON.stringify(items));
};

export const clearLocalCart = () => {
  localStorage.removeItem("guest_cart");
};

export const addItemToCart = createAsyncThunk(
  'cart/addItemToCart',
  async (itemData, { rejectWithValue, getState }) => {
    try {
      const userId = itemData.user_id || getState().auth.user?.id;

      if (!userId) {
        // Save to localStorage instead
        const guestItems = getLocalCart();
        const newItem = {
          ...itemData,
          id: Date.now(), // Temporary ID for local cart items
        };
        const updatedItems = [...guestItems, newItem];
        saveLocalCart(updatedItems);
        return newItem; // return for UI
      }

      const formData = new FormData();

      formData.append('user_id', userId);
      formData.append('product_id', itemData.product_id);
      formData.append('quantity', itemData.quantity);
      formData.append('price', itemData.price);

      if (itemData.message) formData.append('message', itemData.message);
      if (itemData.link) formData.append('link', itemData.link);
      if (itemData.card_id) formData.append('card_id', itemData.card_id);
      if (itemData.card_image) formData.append('card_image', itemData.card_image);
      if (itemData.uploadvideo) formData.append('uploadvideo', itemData.uploadvideo, itemData.uploadvideo.name);

      if (itemData.thumbnail) formData.append('thumbnail', itemData.thumbnail, itemData.thumbnail.name);

      if (itemData.addons && itemData.addons.length > 0) {
        itemData.addons.forEach((addon, index) => {
          formData.append(`addons[${index}][id]`, addon.id);
          formData.append(`addons[${index}][price]`, addon.price);
          formData.append(`addons[${index}][quantity]`, addon.quantity);
        });
      }

      const response = await axios.post(`${baseUrl}/cart/add`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const mergeGuestCartToUserCart = createAsyncThunk(
  'cart/mergeGuestCartToUserCart',
  async (_, { dispatch, getState }) => {
    const user = getState().auth.user;
    const guestCart = getLocalCart();

    if (!user || guestCart.length === 0) return;

    console.log("Merging guest cart items:", guestCart);

    for (const guestItem of guestCart) {
      try {
        const itemWithUser = { ...guestItem, user_id: user.id };

        const result = await dispatch(addItemToCart(itemWithUser));

        if (addItemToCart.fulfilled.match(result)) {
          console.log("✅ Added guest item to user cart:", result.payload);
        } else {
          console.warn("❌ Failed to add guest item:", result.payload || result.error);
        }
      } catch (err) {
        console.error("🔥 Error dispatching guest item:", err);
      }
    }

    clearLocalCart();
    await dispatch(fetchUserCart());
  }
);




export const removeCartItem = createAsyncThunk(
  'cart/removeCartItem',
  async (cartItemId, { rejectWithValue }) => {
    try {
      await axios.delete(`${baseUrl}/cart/cart-items/${cartItemId}`);
      return cartItemId;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const updateCartItem = createAsyncThunk(
  'cart/updateCartItem',
  async (itemData, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      formData.append('cart_item_id', itemData.cart_item_id);
      formData.append('product_id', itemData.product_id);

      if (itemData.message) formData.append('message', itemData.message);
      if (itemData.link) formData.append('link', itemData.link);
      if (itemData.card_id) formData.append('card_id', itemData.card_id);
      if (itemData.card_image) formData.append('card_image', itemData.card_image);
      if (itemData.uploadvideo) formData.append('uploadvideo', itemData.uploadvideo);
      if (itemData.thumbnail) formData.append('thumbnail', itemData.thumbnail);
      if (itemData.record_video) formData.append('record_video', itemData.record_video);

      const response = await axios.post(`${baseUrl}/cart/update-cart_item`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const updateCartItemAddon = createAsyncThunk(
  'cart/updateCartItemAddon',
  async (addonData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${baseUrl}/cart/update-addon`, addonData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return {
        ...response.data,
        price: addonData.price,
        cart_item_id: addonData.cart_item_id,
        addon_id: addonData.addon_id,
        quantity: addonData.quantity
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const removeCartItemAddon = createAsyncThunk(
  'cart/removeCartItemAddon',
  async (addonData, { rejectWithValue }) => {
    try {
      await axios.post(`${baseUrl}/cart/remove-cart-items_addons`, addonData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return { cartItemId: addonData.cart_item_id, addonId: addonData.addon_id };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const moveCartToOrder = createAsyncThunk(
  'cart/moveCartToOrder',
  async (orderData, { rejectWithValue, getState }) => {
    try {
      const userId = orderData.user_id || getState().auth.user?.id;

      if (!userId) {
        return rejectWithValue('User not authenticated');
      }

      const dataWithUserId = {
        ...orderData,
        user_id: userId
      };

      const response = await axios.post(`${baseUrl}/cart/move-to-order`, dataWithUserId, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const checkCoupon = createAsyncThunk(
  'cart/checkCoupon',
  async (couponData, { rejectWithValue, getState }) => {
    try {
      const userId = getState().auth.user?.id;
      if (!userId) {
        return rejectWithValue('User not authenticated');
      }

      const response = await axios.post(`${baseUrl}/v2/coupon`, {
        code: couponData.code,
        user_id: userId,
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);


const initialState = {
  items: [],
  loading: false,
  error: null,
  subtotal: 0,
  // tax: 0,
  total: 0,
  userId: null,
  couponCode: null,
  couponId: null,
  discount: 0,
};

const calculateItemTotal = (item) => {

  let itemTotal = parseFloat(item.price || 0) * parseInt(item.quantity || 0);

  if (item.addons && item.addons.length > 0) {
    let addonTotal = 0;

    item.addons.forEach(addon => {
      let addonPrice = 0;
      if (addon.addon && addon.addon.price) {
        addonPrice = parseFloat(addon.addon.price);
      } else {
        addonPrice = parseFloat(addon.price || 0);
      }

      const addonQuantity = parseInt(addon.quantity || 0);
      const thisAddonTotal = addonPrice * addonQuantity;


      addonTotal += thisAddonTotal;
    });
    itemTotal += addonTotal;
  }

  return itemTotal;
};

const recalculateCart = (state) => {
  state.subtotal = state.items.reduce((sum, item) => {
    return sum + calculateItemTotal(item);
  }, 0);

  if (state.couponCode && state.discount > 0) {
    state.total = state.subtotal - state.discount;
  } else {
    state.total = state.subtotal;
  }
};

// Create slice
const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    updateQuantity: (state, action) => {
      const { cartItemId, quantity } = action.payload;
      const item = state.items.find(item => item.id === cartItemId);
      if (item) {
        item.quantity = quantity;
        recalculateCart(state);
      }
    },
    applyCoupon: (state, action) => {
      const { code, discount } = action.payload;
      state.couponCode = code;
      state.discount = discount;
      recalculateCart(state);
    },
    clearCart: (state) => {
      state.items = [];
      state.subtotal = 0;
      // state.tax = 0;
      state.total = 0;
      state.couponCode = null;
      state.discount = 0;
    },
    setUserId: (state, action) => {
      state.userId = action.payload;
    },
    syncUserIdFromAuth: (state, action) => {
      state.userId = action.payload?.id || null;
    },
    saveGuestCartToStorage: (state) => {
      localStorage.setItem('guest_cart', JSON.stringify(state.items));
    },
    loadGuestCartFromStorage: (state) => {
      const savedCart = JSON.parse(localStorage.getItem('guest_cart') || '[]');
      state.items = savedCart;
      recalculateCart(state);
    },
    clearGuestCartStorage: () => {
      localStorage.removeItem('guest_cart');
    },
    addGuestItem: (state, action) => {
      const newItem = action.payload;
      const existingItemIndex = state.items.findIndex(item =>
        item.product_id === newItem.product_id &&
        JSON.stringify(item.addons || []) === JSON.stringify(newItem.addons || [])
      );

      if (existingItemIndex !== -1) {
        state.items[existingItemIndex].quantity += newItem.quantity;
      } else {
        state.items.push(newItem);
      }

      recalculateCart(state);
    },
    removeGuestItem: (state, action) => {
      state.items = state.items.filter(item => item.id !== action.payload);
      localStorage.setItem('guest_cart', JSON.stringify(state.items));
      recalculateCart(state);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserCart.fulfilled, (state, action) => {
        state.loading = false;
        const cartData = action.payload.data?.[0];

        if (cartData) {
          state.items = cartData.items || [];
          state.userId = cartData.user_id || null;
        } else {
          state.items = [];
          state.userId = null;
        }

        recalculateCart(state);
      })
      .addCase(fetchUserCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch cart';
      })

      .addCase(addItemToCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addItemToCart.fulfilled, (state, action) => {

        state.loading = false;
        state.items.push(action.payload);
        recalculateCart(state);
      })
      .addCase(addItemToCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to add item to cart';
      })

      .addCase(removeCartItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeCartItem.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(item => item.id !== action.payload);
        recalculateCart(state);
      })
      .addCase(removeCartItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to remove item from cart';
      })

      .addCase(updateCartItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCartItem.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        recalculateCart(state);
      })
      .addCase(updateCartItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update cart item';
      })

      .addCase(updateCartItemAddon.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCartItemAddon.fulfilled, (state, action) => {
        state.loading = false;

        const { cart_item_id, addon_id, quantity, price } = action.payload;

        const itemIndex = state.items.findIndex(item => item.id === cart_item_id);

        if (itemIndex !== -1) {
          const item = state.items[itemIndex];

          if (!item.addons) {
            item.addons = [];
          }

          const addonIndex = item.addons.findIndex(addon =>
            addon.id === addon_id || addon.addon_id === addon_id
          );

          if (addonIndex !== -1) {
            const existingAddon = item.addons[addonIndex];

            item.addons[addonIndex] = {
              ...existingAddon,
              quantity: quantity,
              price: price
            };
          } else {
            item.addons.push({
              id: addon_id,
              addon_id: addon_id,
              quantity: quantity,
              price: price
            });
          }
        }

        recalculateCart(state);
      })


      .addCase(removeCartItemAddon.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeCartItemAddon.fulfilled, (state, action) => {
        state.loading = false;
        const { cartItemId, addonId } = action.payload;
        const itemIndex = state.items.findIndex(item => item.id === cartItemId);

        if (itemIndex !== -1 && state.items[itemIndex].addons) {
          state.items[itemIndex].addons = state.items[itemIndex].addons.filter(
            addon => addon.id !== addonId && addon.addon_id !== addonId
          );
        }

        recalculateCart(state);
      })
      .addCase(removeCartItemAddon.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to remove cart item addon';
      })
      .addCase(checkCoupon.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkCoupon.fulfilled, (state, action) => {
        state.loading = false;

        const payload = action.payload;

        if (payload.valid && payload.data?.coupon) {
          const coupon = payload.data.coupon;

          state.couponId = coupon.id;
          state.couponCode = coupon.code;

          if (coupon.is_percent) {
            const percent = parseFloat(coupon.discount || 0);
            state.discount = (state.subtotal * percent) / 100;
          } else {
            state.discount = parseFloat(coupon.discount || 0);
          }

          recalculateCart(state);
        }
        // ❗ if coupon is not valid, don't do anything (user sees error from UI)
      })

      .addCase(checkCoupon.rejected, (state, action) => {
        state.loading = false;
        state.error = typeof action.payload === 'string' ? action.payload
          : (action.payload?.message || 'Failed to apply coupon');
      })

      .addCase(moveCartToOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(moveCartToOrder.fulfilled, (state) => {
        state.loading = false;
        state.items = [];
        state.subtotal = 0;
        // state.tax = 0;
        state.total = 0;
        state.couponCode = null;
        state.discount = 0;
      })
      .addCase(moveCartToOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = typeof action.payload === "string" ? action.payload : (action.payload?.message || "An error occurred");
      });
  },
});

export const {
  updateQuantity,
  applyCoupon,
  clearCart,
  setUserId,
  syncUserIdFromAuth,
  saveGuestCartToStorage,
  loadGuestCartFromStorage,
  clearGuestCartStorage,
  addGuestItem,
  removeGuestItem,
} = cartSlice.actions;

export default cartSlice.reducer;

// Selectors
export const selectCartItems = (state) => state.cart.items;
export const selectCartTotal = (state) => state.cart.total;
export const selectCartSubtotal = (state) => state.cart.subtotal;
// export const selectCartTax = (state) => state.cart.tax;
export const selectCartDiscount = (state) => state.cart.discount;
export const selectCartCouponId = (state) => state.cart.couponId;
export const selectCartCouponCode = (state) => state.cart.couponCode;
export const selectCartLoading = (state) => state.cart.loading;
export const selectCartError = (state) => state.cart.error;
export const selectCartUserId = (state) => state.cart.userId || state.auth.user?.id;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectAuthUser = (state) => state.auth.user;
export const selectCartItemCount = (state) => state.cart.items.reduce((count, item) => count + item.quantity, 0);
export const selectHasCartItems = (state) => state.cart.items.length > 0;